// src/utils/theme.js
import { createTheme, alpha } from '@mui/material/styles';

const PRIMARY = '#91ce0f';

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main:         PRIMARY,
      light:        '#3a9de0',
      dark:         '#0a5a8f',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00a882',
      dark: '#007a60',
    },
    background: {
      default: '#f0f8f4',
      paper:   '#ffffff',
    },
    divider: 'rgba(16, 117, 186, 0.12)',
    text: {
      primary:   '#0f2137',
      secondary: '#4a6a85',
    },
    success: { main: '#00a882' },
    warning: { main: '#e07b00' },
    error:   { main: '#d32f2f' },
  },
  typography: {
    fontFamily: '"DM Sans", "Helvetica Neue", sans-serif',
    h1: { fontFamily: '"Sora", sans-serif', fontWeight: 700 },
    h2: { fontFamily: '"Sora", sans-serif', fontWeight: 700 },
    h3: { fontFamily: '"Sora", sans-serif', fontWeight: 600 },
    h4: { fontFamily: '"Sora", sans-serif', fontWeight: 600 },
    h5: { fontFamily: '"Sora", sans-serif', fontWeight: 600 },
    h6: { fontFamily: '"Sora", sans-serif', fontWeight: 600 },
    button: { fontFamily: '"DM Sans", sans-serif', fontWeight: 600, letterSpacing: '0.02em' },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&family=DM+Sans:wght@400;500;600&display=swap');
        body { background: #f0f8f4; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #e8eef4; }
        ::-webkit-scrollbar-thumb { background: ${alpha(PRIMARY, 0.3)}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${alpha(PRIMARY, 0.55)}; }
      `,
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderRadius: 16,
          border: '1px solid rgba(16, 117, 186, 0.1)',
          boxShadow: '0 1px 4px rgba(15,33,55,0.06)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: '#ffffff',
          border: '1px solid rgba(16, 117, 186, 0.1)',
          borderRadius: 16,
          backgroundImage: 'none',
          boxShadow: '0 2px 8px rgba(15,33,55,0.06)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 22px',
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${PRIMARY} 0%, #5f940a 100%)`,
          boxShadow: `0 4px 14px ${alpha(PRIMARY, 0.3)}`,
          '&:hover': {
            background: `linear-gradient(135deg, #5f940a0%, ${PRIMARY} 100%)`,
            boxShadow: `0 6px 20px ${alpha(PRIMARY, 0.45)}`,
          },
        },
        outlinedPrimary: {
          borderColor: alpha(PRIMARY, 0.4),
          '&:hover': { background: alpha(PRIMARY, 0.05), borderColor: PRIMARY },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            background: '#ffffff',
            '& fieldset': { borderColor: 'rgba(16, 117, 186, 0.2)' },
            '&:hover fieldset': { borderColor: alpha(PRIMARY, 0.5) },
            '&.Mui-focused fieldset': { borderColor: PRIMARY },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: { borderRadius: 10, background: '#ffffff' },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 500 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          background: '#f5f8fc',
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: '#4a6a85',
          borderBottom: '1px solid rgba(16,117,186,0.1)',
        },
        body: {
          borderBottom: '1px solid rgba(16,117,186,0.06)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: '#0f2137',
          borderRadius: 8,
          fontSize: '0.75rem',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { backgroundImage: 'none' },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            background: alpha(PRIMARY, 0.1),
            color: PRIMARY,
          },
        },
      },
    },
  },
});

export default theme;
